import { FC } from 'react';

import { AddDocumentIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { useFormat } from '../../../../hooks/index';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { ShareDeltaBadge } from '../../components/share-delta-badge';
import { isPoolEvent, resolvePoolEventRow } from '../../utils/pool-event-row.util';
import {
  mergeEventAndRecordFileLinks,
  resolveTransactionRowDocumentLinks,
} from '../../utils/resolve-event-document-links.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

export const PoolEvent: FC<CardProps> = ({ event, openFileUploadModal }) => {
  const { format } = useFormat();
  if (!isPoolEvent(event)) return <></>;
  const { pool } = event;
  const row = resolvePoolEventRow(event);
  const { allLinks: documentFileLinks } = resolveTransactionRowDocumentLinks(
    mergeEventAndRecordFileLinks(event),
    {
      docusignFileLinks: event.docusignFileLinks,
      event,
      linkedSignatureFileLink: event.signatureSummary?.linkedSignatureFileLink,
    },
  );

  return (
    <div className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm">
      <TransactionTitleItem createdAt={format(row.date, 'dd/MM/yyyy')} name={pool?.name} />
      <TransactionOverviewItem
        title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
        value={<AppFormattedMessage id={row.transactionType} />}
      />
      <TransactionOverviewItem
        title={
          <AppFormattedMessage
            id={
              row.delta === null ? 'NO_OF_FULLY_DILUTED_SHARES' : 'NEW_NO_OF_FULLY_DILUTED_SHARES'
            }
          />
        }
        value={
          // Wraps the badge under the number when both do not fit: the overview item clips.
          <span className="inline-flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
            {row.sharesCount.toLocaleString('en-US')}
            {row.delta !== null && <ShareDeltaBadge delta={row.delta} />}
          </span>
        }
      />
      <TransactionOverviewItem
        title={<AppFormattedMessage id="SHARE_CLASS" />}
        value={pool?.shareClass?.name}
      />

      {documentFileLinks.length > 0 && (
        <TransactionOverviewItem
          title={<AppFormattedMessage id="DOCUMENTS" />}
          value={
            <HoverCard>
              <HoverCardTrigger onClick={() => openFileUploadModal(documentFileLinks)}>
                <AddDocumentIcon className="cursor-pointer" />
              </HoverCardTrigger>

              <HoverCardContent
                className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                side="top"
              >
                <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                  {documentFileLinks.length === 1
                    ? getS3FileOriginalName(documentFileLinks[0])
                    : `Download ${documentFileLinks.length} files`}
                </span>
              </HoverCardContent>
            </HoverCard>
          }
        />
      )}
    </div>
  );
};
